import { useCallback, useEffect, useState } from 'react';
import type { WidgetId, WidgetState } from '@rabbitfuel/shared';

/**
 * Was ein Zuschauer fuer sich selbst ausblenden kann: die Einblendungen der
 * Regie, das kleine Fenster und das Quellenschild. Dazu, weiter unten, wie
 * gross er die Einblendungen sehen will.
 *
 * Das gilt nur auf diesem Geraet. Der Stream fuer alle anderen -- auch der
 * RTMP-Ausgang, der die Renderseite aufnimmt -- bleibt, wie die Regie ihn
 * stellt. Deshalb liegt es im localStorage und geht nie an den Server.
 */
export type ViewerLayer = WidgetId | 'pip' | 'source';

const KEY = 'rabbitfuel:watch:hidden';

/*
 * Gemerkt wird, was *aus* ist, nicht was an ist: schaltet die Regie spaeter
 * etwas Neues ein, ist es erst einmal zu sehen -- so, wie sie es zeigt.
 */
function load(): Set<ViewerLayer> {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return new Set(Array.isArray(list) ? list.filter((v): v is ViewerLayer => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function useHiddenLayers() {
  const [hidden, setHidden] = useState<ReadonlySet<ViewerLayer>>(load);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify([...hidden]));
    } catch {
      /* Private Mode o.ae. -- dann gilt es eben nur bis zum Neuladen. */
    }
  }, [hidden]);

  const toggle = useCallback((id: ViewerLayer) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((ids: ViewerLayer[], hide: boolean) => {
    setHidden((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (hide) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  return { hidden, toggle, setMany };
}

/* ------------------------------------------------------ Groesse je Zuschauer */

/**
 * Wie gross ein Zuschauer die Einblendungen fuer sich sehen will -- fuer alle,
 * denen die Groesse der Regie zu klein ist. Ein Faktor je Einblendung auf die
 * Groesse, die die Regie gewaehlt hat; 1 heisst „wie die Regie". Gilt wie das
 * Ausblenden nur auf diesem Geraet und nie im Stream.
 */
export type LayerZoom = Partial<Record<WidgetId, number>>;

/** Die Stufen fuer − und +. 2 ist genug, um eine kleine Uhr gut lesbar zu machen. */
export const ZOOM_STEPS = [0.75, 0.9, 1, 1.15, 1.3, 1.5, 1.75, 2] as const;

const ZOOM_KEY = 'rabbitfuel:watch:zoom';

function loadZoom(): LayerZoom {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(ZOOM_KEY) ?? '{}');
    if (!raw || typeof raw !== 'object') return {};
    const out: LayerZoom = {};
    for (const [id, value] of Object.entries(raw)) {
      if (typeof value === 'number' && (ZOOM_STEPS as readonly number[]).includes(value)) out[id as WidgetId] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function useLayerZoom() {
  const [zoom, setZoom] = useState<LayerZoom>(loadZoom);

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_KEY, JSON.stringify(zoom));
    } catch {
      /* Private Mode o.ae. -- dann gilt es eben nur bis zum Neuladen. */
    }
  }, [zoom]);

  /** Eine Stufe groesser (+1) oder kleiner (−1); am Rand bleibt es stehen. */
  const step = useCallback((id: WidgetId, direction: 1 | -1) => {
    setZoom((prev) => {
      const index = ZOOM_STEPS.indexOf((prev[id] ?? 1) as (typeof ZOOM_STEPS)[number]);
      const nextIndex = Math.min(ZOOM_STEPS.length - 1, Math.max(0, (index < 0 ? 2 : index) + direction));
      const value = ZOOM_STEPS[nextIndex];
      const next = { ...prev };
      // „Wie die Regie" wird nicht gespeichert -- so bleibt die Liste leer, bis
      // jemand wirklich etwas aendert.
      if (value === 1) delete next[id];
      else next[id] = value;
      return next;
    });
  }, []);

  const reset = useCallback(() => setZoom({}), []);

  return { zoom, step, reset };
}

/** Die Einblendungen der Regie ohne das, was dieser Zuschauer ausgeblendet hat. */
export function withoutHidden(widgets: WidgetState, hidden: ReadonlySet<ViewerLayer>): WidgetState {
  if (hidden.size === 0) return widgets;
  const next = { ...widgets };
  for (const id of Object.keys(next) as WidgetId[]) {
    if (hidden.has(id) && next[id].on) next[id] = { ...next[id], on: false };
  }
  return next;
}
